/**
 * Published prices.
 *
 * These figures are signed off and are what the site advertises — treat an
 * edit here as a commercial decision, not a copy tweak.
 *
 * This is marketing copy, NOT a billing source of truth. Nothing charges money
 * from these values. When real billing arrives it gets its own immutable
 * plan_prices table; this file stays presentational.
 *
 * Amounts are integers in MAJOR units (USD 97 -> 97). Never minor units:
 * a x100 convention invites 100x display bugs.
 */

export const PRICES_APPROVED = true;

/**
 * Single currency for now. The cards and the currency picker read from this
 * map rather than hardcoding a symbol, so restoring KES/UGX later means adding
 * entries here plus the matching per-tier amounts — the picker un-hides itself
 * automatically once there is more than one currency.
 */
export const CURRENCIES = {
  USD: { code: 'USD', symbol: '$', country: 'US dollar' },
};

export const DEFAULT_CURRENCY = 'USD';

/**
 * Yearly billing is published: two months free (Starter $970, Growth $1,290).
 * The toggle derives its "save N%" badge from YEARLY_MONTHS_CHARGED, so the
 * badge and the prices cannot drift apart.
 */
export const YEARLY_BILLING_ENABLED = true;

/** Yearly billing is charged as 10 months — i.e. two months free. */
export const YEARLY_MONTHS_CHARGED = 10;

/*
 * One subscription covers one farm, so the tiers differ by flock size, team
 * size and what the system does — not by a farm count.
 *
 * Every feature named here must exist in the app (see the Chickrah product
 * overview). Payroll and staff records were removed from the product, so they
 * are gone from the cards too; advertising a module a customer cannot find
 * after paying is the fastest way to lose them.
 *
 * `monthly: null` makes a tier Custom (quoted per farm), with `quoteNote`
 * under it.
 *
 * Wording note: nothing here says "IoT". Farmers buy sensors that read
 * temperature, humidity and ammonia. Naming the readings sells the thing;
 * naming the category explains nothing to the person paying for it.
 */
export const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    blurb: 'For a growing farm getting off paper.',
    birdCap: 'Up to 5,000 birds',
    users: 'Up to 3 staff logins',
    featured: false,
    monthly: { USD: 97 },
    features: [
      'Dashboard & flock management',
      'Daily entry on your phone',
      'Egg Store with daily egg ledger',
      'Feed Store with days-of-feed-left',
      'Vaccination schedule & reminders',
      'Mortality, feed & water alerts',
      'Printable PDF & Excel reports',
      'Monthly AI farm report',
      'Sensor alerts: temperature, ammonia & water',
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    blurb: 'Several houses, and the real cost of every tray.',
    birdCap: 'Up to 20,000 birds',
    users: 'Up to 10 staff logins',
    featured: true,
    monthly: { USD: 129 },
    features: [
      'Everything in Starter',
      'Cost of production per tray',
      'Feed formulas & mixing plan',
      'Expenses with receipt photos',
      'Multiple egg stores & transfers',
      'Import past records from Excel',
      'Full audit log of every change',
      'Sensor history & trend charts',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    blurb: 'Large or multi-site operations.',
    birdCap: 'Unlimited birds',
    users: 'Unlimited staff logins',
    featured: false,
    // Custom: no published price, quoted per farm. quoteNote gives the
    // starting point so "Custom" does not read as "expensive, don't ask".
    monthly: null,
    quoteNote: 'From $299 a month, quoted on your flock size',
    features: [
      'Everything in Growth',
      'Your farm’s name & logo on the app',
      'Past records migrated for you',
      'Priority onboarding & staff training',
      'Direct line to the team',
    ],
  },
];

/**
 * The sensor add-on, shown once under the cards. Every plan can use it, so it
 * sits outside the tiers rather than being a reason to upgrade. The hardware is
 * a one-time cost, which is why no price is published here.
 */
export const SENSORS = {
  heading: 'Smart sensors for your poultry house',
  sub: 'Add them to any plan. A one-time hardware cost, and the readings flow straight into Chickrah.',
  readings: [
    { name: 'Temperature & humidity', note: 'Keeps chicks and layers in their safe range' },
    { name: 'Ammonia', note: 'Warns you when the air turns harmful' },
    { name: 'Water level', note: 'Alerts you before the tank runs dry' },
  ],
  points: [
    'Alerts to your phone the moment a reading goes wrong',
    'Beeps on site, even without internet',
    'Readings saved offline and sent when the network returns',
  ],
};

/** Shown once under the cards instead of repeating on every plan. */
export const INCLUDED_EVERYWHERE = [
  'Works on slow 2G networks',
  'Phone, tablet or computer',
  'Your currency & tray size',
  'Staff permissions',
  'Daily backups',
  'Your data kept private',
];

export const FAQ = [
  {
    q: 'Do I need the sensors to use Chickrah?',
    a: 'No. Every plan works entirely on records you enter yourself. Sensors for temperature, humidity, ammonia and water level are an optional one-time hardware cost. They work with every plan and can be added at any time.',
  },
  {
    q: 'What happens to the records I already keep on paper?',
    a: 'We load them during onboarding. That is part of why we start with a demo call rather than a self-serve signup. Your history is what makes the reports useful on day one.',
  },
  {
    q: 'Can I change plan later?',
    a: 'Yes. Plans are matched to the number of birds and staff on your farm, so moving up as you grow is expected and takes effect immediately.',
  },
  {
    q: 'Is there a discount for paying yearly?',
    a: 'Yes. Paying yearly gives you two months free: Starter is $970 a year and Growth is $1,290 a year.',
  },
  {
    q: 'How do I pay?',
    a: 'Mobile money, card or bank transfer. We set this up with you on the onboarding call.',
  },
];
